import { INSTAGRAM_CAROUSEL_RATIO_MAX, INSTAGRAM_CAROUSEL_RATIO_MIN } from '@mondaygpt/constants'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { InstagramMediaItem } from '../types'
import { carouselSlideFocusKey } from '../utils/carousel-slide-focus'
import { SectionHoverWrapper } from './SectionHoverWrapper'

const NAV_BTN =
  'border-line bg-bg-3 text-paper-dim hover:text-paper hover:bg-bg-4 flex h-7 w-7 shrink-0 cursor-pointer items-center justify-center rounded-full border transition-colors disabled:pointer-events-none disabled:opacity-30'

interface CarouselMediaPreviewProps {
  items: InstagramMediaItem[]
  altText?: string | null
  onAskAISlide?: (idx: number) => void
  onEditSlide?: (idx: number) => void
  activeEditingSlide?: number | null
}

export function CarouselMediaPreview({
  items,
  altText,
  onAskAISlide,
  onEditSlide,
  activeEditingSlide,
}: CarouselMediaPreviewProps) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const scroller = scrollerRef.current
    if (!scroller) return

    const observer = new IntersectionObserver(
      entries => {
        const visible = entries
          .filter(e => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0]
        if (!visible) return
        const idx = slideRefs.current.findIndex(el => el === visible.target)
        if (idx >= 0) setActiveIndex(idx)
      },
      { root: scroller, threshold: [0.6] }
    )

    slideRefs.current.forEach(el => el && observer.observe(el))
    return () => observer.disconnect()
  }, [items.length])

  // Scroll the inner snap container directly — never scrollIntoView, which
  // propagates up to scrollable ancestors and, inside the composer/PostModal,
  // either no-ops (slide deemed already in view) or scrolls a vertical ancestor
  // instead of this horizontal scroller — so prev/next/dots appear to do nothing.
  // See the sibling CarouselAsset in AssetMedia.tsx, which already uses this.
  const scrollTo = useCallback((idx: number) => {
    const scroller = scrollerRef.current
    const target = slideRefs.current[idx]
    if (!scroller || !target) return
    scroller.scrollTo({ left: target.offsetLeft, behavior: 'smooth' })
  }, [])

  const handlePrev = useCallback(
    () => scrollTo(Math.max(0, activeIndex - 1)),
    [activeIndex, scrollTo]
  )
  const handleNext = useCallback(
    () => scrollTo(Math.min(items.length - 1, activeIndex + 1)),
    [activeIndex, items.length, scrollTo]
  )

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'ArrowLeft') {
      e.preventDefault()
      handlePrev()
    } else if (e.key === 'ArrowRight') {
      e.preventDefault()
      handleNext()
    }
  }

  const canHoverEdit = !!(onAskAISlide || onEditSlide)

  // Mirror Instagram: container takes the first slide's ratio, clamped to [4:5, 1.91:1].
  const aspectRatio = useMemo(() => {
    const first = items[0]?.ratio
    if (!first || !Number.isFinite(first)) return 1
    return Math.min(INSTAGRAM_CAROUSEL_RATIO_MAX, Math.max(INSTAGRAM_CAROUSEL_RATIO_MIN, first))
  }, [items])

  return (
    <div
      tabIndex={0}
      onKeyDown={handleKeyDown}
      className="relative flex w-full flex-col focus:outline-none"
    >
      <div className="relative w-full" style={{ aspectRatio: String(aspectRatio) }}>
        <div
          ref={scrollerRef}
          className="flex h-full w-full snap-x snap-proximity overflow-x-auto scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          {items.map((item, idx) => {
            const slide = (
              <img
                src={item.url}
                alt={item.altText || altText || `Slide ${idx + 1}`}
                className="h-full w-full object-cover"
                draggable={false}
              />
            )
            return (
              <div
                key={item.attachmentId || `${item.url}-${idx}`}
                ref={el => {
                  slideRefs.current[idx] = el
                }}
                className="relative h-full w-full flex-shrink-0 snap-start"
              >
                {canHoverEdit ? (
                  <SectionHoverWrapper
                    fieldId={carouselSlideFocusKey(idx)}
                    onAskAI={() => onAskAISlide?.(idx)}
                    onEdit={() => onEditSlide?.(idx)}
                    isActiveEditing={activeEditingSlide === idx}
                  >
                    {slide}
                  </SectionHoverWrapper>
                ) : (
                  slide
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Slide navigation dock — below the image, never overlaid on it, mirroring the
          video editor timeline / carousel filmstrip (bg-bg-2 bar under the canvas). */}
      <div className="flex w-full items-center gap-2 border-t border-line bg-bg-2 px-2 py-1.5">
        <button
          type="button"
          onClick={handlePrev}
          disabled={activeIndex === 0}
          aria-label="Previous slide"
          className={NAV_BTN}
        >
          <ChevronLeft size={16} />
        </button>

        <div className="flex flex-1 items-center justify-center gap-1.5">
          {items.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => scrollTo(idx)}
              aria-label={`Go to slide ${idx + 1}`}
              aria-current={idx === activeIndex}
              // Inline var(--paper) instead of a Tailwind bg utility: Tailwind v4 +
              // @theme inline drops some stock utilities (bg-white rendered transparent
              // here once — see CarouselMediaPreview history).
              style={{
                backgroundColor: 'var(--paper)',
                opacity: idx === activeIndex ? 1 : 0.3,
              }}
              className="h-1.5 w-1.5 shrink-0 rounded-full transition-all hover:scale-125"
            />
          ))}
        </div>

        <span className="shrink-0 text-[11px] font-medium text-paper-dim tabular-nums">
          {activeIndex + 1}/{items.length}
        </span>

        <button
          type="button"
          onClick={handleNext}
          disabled={activeIndex >= items.length - 1}
          aria-label="Next slide"
          className={NAV_BTN}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  )
}
